import { FC, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import ChildCard from "@src/components/shared-layouts/ChildCard";
import CustomDataTable from "@src/components/shared-ui-components/CustomDataTable";
import { TypographyH5 } from "@src/components/shared-ui-components/CustomTypography";
import { Flex } from "@src/components/shared-ui-components/Flex";
import LocationContext from "@src/context/location/locationContext";

import checkCloseTableContext from "@src/context/CheckCloseInformationTable/checkCloseTableContext";
import ShowErrorToastContext from "@context/showErrorToast/showErrorToastContext";
import { DEVICE, useWindowSizeChanged } from "@hooks/DeviceSize";
import { CustomSmallCardWrapper } from "../../HomeDashboard/style";
import { closedColumns, getCheckData, refinedRowData } from "./helper";
import { CheckWrapper, TableWrapper } from "./style";

interface CheckTableType {
  pagination?: boolean;
}

const CloseCheckTable: FC<CheckTableType> = ({ pagination = false }) => {
  const [rowData, setRowsData] = useState<[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  const filterBy = "closed";
  const title = "Closed";
  const navigateUrl = "/check-information/closed-checks";
  const errorMessage = "Unable to Fetch Close Check";

  const [{ checkCloseTableState, checkCloseTableSetState }] = useContext(
    checkCloseTableContext
  );

  const { identifier, sortingType, page } = checkCloseTableState;

  const [{ locationState }] = useContext(LocationContext);
  const { property, store, revenueCenter, date } = locationState;
  const [{ showErrorToastState, showErrorToastSetState }] = useContext(
    ShowErrorToastContext
  );
  const sortBy = {
    tableName: "",
    label: identifier,
    order: sortingType,
  };

  const sortHandler = (_: string, sortId: string) => {
    checkCloseTableSetState((pre) => {
      return {
        ...pre,
        identifier: sortId,
        sortingType: pre.sortingType === "desc" ? "asc" : "desc",
      };
    });
  };

  const handleNavigate = () => {
    navigate(navigateUrl);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    checkCloseTableSetState((pre) => {
      return { ...pre, page: newPage - 1 };
    });
  };

  const getData = async (customFilter: CustomFilterType) => {
    localStorage.setItem(
      "closeCheckTableFilter",
      JSON.stringify({
        sortingType: customFilter.sortingType,
        page: customFilter.page,
        identifier: customFilter.identifier,
      })
    );
    try {
      const res = await getCheckData(customFilter);
      if (!res.error) {
        setRowsData(res?.data);
      } else {
        showErrorToastSetState({
          errorMessage: res.error || "",
          errorTitle: errorMessage,
          open: true,
        });
      }
    } catch (err) {
      showErrorToastSetState({
        ...showErrorToastState,
        errorTitle: errorMessage,
        open: true,
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    const customFilter: CustomFilterType = {
      property,
      store,
      rvc: revenueCenter,
      date,
      identifier,
      sortingType,
      page: pagination ? page : 0,
      filterBy,
    };
    getData(customFilter);
  }, [property, store, revenueCenter, date, identifier, sortingType, page]);

  const rowDataTable = refinedRowData(rowData);

  const device = useWindowSizeChanged();
  const isDesktop = device === DEVICE.DESKTOP;

  return (
    <CustomSmallCardWrapper>
      <ChildCard title={`${title} Checks`} hideDivider isDataTable>
        <CheckWrapper isDesktop={isDesktop} isNotEmpty={!!rowDataTable?.length}>
          <Flex>
            <strong>Note:&nbsp;</strong>
            <TypographyH5 className="note">
              View check details by selecting check #.
            </TypographyH5>
          </Flex>
          <TableWrapper>
            <CustomDataTable
              sortHandler={sortHandler}
              rowData={rowDataTable}
              page={page}
              showPagination={pagination}
              sortBy={sortBy}
              handleNavigate={handleNavigate}
              handlePageChange={handleChangePage}
              isLoading={isLoading}
              dyColumns={closedColumns}
            />
          </TableWrapper>
        </CheckWrapper>
      </ChildCard>
    </CustomSmallCardWrapper>
  );
};

CloseCheckTable.defaultProps = {
  pagination: false,
};

export default CloseCheckTable;
